import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import {issuesFetchParamsType, issueWrapperArrayType} from '../../constants/proptype-validation';
import isNullOrUndefined from '@adsk/bim360-matrix-react-helpers/es/isNullOrUndefined';
import Button from '@adsk/bim360-matrix-react-button';
import Text from '@adsk/bim360-matrix-react-text';
import ExportModal from '@adsk/bim360-matrix-react-export-modal';
import IssuesList from './IssuesList';
import IssuesPanelHeaderContainer from './IssuesPanelHeaderContainer';
import IssueFetchError from '../lib/IssueFetchError';
import i18n from '../../utils/i18n';
import {documentType} from '../../constants/proptype-validation/issue-proptypes';
import ProjectSetupInProgressPlaceholder from '../common/ProjectSetupInProgressPlaceholder';
import {TYPE_QUALITY_ISSUES} from '../../constants/issue-types';
import {toIsoDate} from '../../utils/date-helpers';
import {exportList} from '../../utils/analytics-helpers';
import {linkedDocumentUrn, getListReportDataObject, ReportsConfig} from '../../utils/reportHelpers';
import {REPORT_TYPES} from '../../constants/ReportService';
import {trimFileName} from '../../utils/notificationHelpers';
import {reportFormats} from '../../constants/analytics';
import featureFlags, {FEATURE_FLAGS} from '../../utils/featureFlags';

export default class IssuesPanel extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      exportModalType: ''
    };
    this.modalTitles = {
      [reportFormats.XLSX]: i18n.t('export.exportXlsx'),
      [reportFormats.PDF]: i18n.t('export.exportPdf')
    };
  }
  componentDidUpdate(prevProps) {
    const {fetchParams, fetchIssuesAction} = this.props;
    if (prevProps.fetchParams !== fetchParams) {
      fetchIssuesAction({type: TYPE_QUALITY_ISSUES});
    }
  }

  renderList(issues) {
    const {changeLocationAction, loading} = this.props;

    return <IssuesList issues={issues} loading={loading} changeLocationAction={changeLocationAction} />;
  }

  renderFooter() {
    const {pushpinWorkflowEnabled, changeLocationAction, document, canCreateIssue} = this.props;
    const isViewingDocumentLatestVersion =
      !isNullOrUndefined(document.latestVersion) && document.latestVersion !== document.version;

    return (
      <div className="IssuesPanel__toolbar">
        <Button
          className="IssuesPanel__create-button"
          data-testid="IssuesPanel__create-button"
          styleType={Button.StyleType.PRIMARY}
          onClick={() => changeLocationAction(`/${TYPE_QUALITY_ISSUES}/new`)}
          disabled={isViewingDocumentLatestVersion || !pushpinWorkflowEnabled || !canCreateIssue}
        >
          {i18n.t('navigation.create', {issueType: TYPE_QUALITY_ISSUES})}
        </Button>
      </div>
    );
  }

  renderExportModal(formatType) {
    const {trackEvent, createReportAction, fetchParams, viewType, isCreatingReport} = this.props;
    const queryParams = {
      ...fetchParams.queryParams,
      ...fetchParams.hiddenQueryParams,
      linkedDocumentUrn: linkedDocumentUrn(fetchParams.targetUrn, fetchParams.documentVersion)
    };
    const textIssueType = i18n.t(['issue.label_capitalized', 'issueType.issues.label_capitalized']);
    const textReport = i18n.t('export.report');
    const textToday = toIsoDate(Date.now());

    return (
      <ExportModal
        show={this.state.exportModalType === formatType}
        container={this}
        modalTitle={this.modalTitles[formatType]}
        title={`${textIssueType} ${textReport} ${textToday}`}
        onCreate={({title, message}) => {
          const reportConfig = ReportsConfig[REPORT_TYPES.QUALITY_ISSUES];
          const fileType = formatType.toLocaleLowerCase();
          const reportData = getListReportDataObject(title, message, reportConfig, queryParams, fileType);
          const failureMessage = (
            <Text hasHTML>
              {trimFileName(i18n.t('notifications.failedToDownload', {fileName: `${title}.${fileType}`}))}
            </Text>
          );
          const reportMetadata = {fileType: formatType};
          trackEvent(exportList(formatType, viewType));
          createReportAction(reportData, failureMessage, reportMetadata).then(() =>
            this.setState({exportModalType: ''})
          );
        }}
        onCancel={() => this.setState({exportModalType: ''})}
        onExited={() => this.setState({exportModalType: ''})}
        creating={isCreatingReport}
      />
    );
  }

  render() {
    const {issues, loading, error, shouldShowProjectSetupInProgressPlaceholder} = this.props;

    if (shouldShowProjectSetupInProgressPlaceholder) {
      return <ProjectSetupInProgressPlaceholder />;
    }
    const isDocViewPdfAndXlsxReportEnabled = featureFlags.get(FEATURE_FLAGS.DOC_VIEW_PDF_AND_XLSX_EXPORT);
    return (
      <div className="IssuesPanel">
        <IssuesPanelHeaderContainer
          container={this}
          onExportOptionClick={formatType => this.setState({exportModalType: formatType})}
        />

        <IssueFetchError error={error} />

        <div className="IssuesPanel__list-animation-container">
          {issues.length > 0 || loading ? (
            this.renderList(issues)
          ) : (
            <p className="IssuesPanel__empty">{i18n.t('none', {issueType: TYPE_QUALITY_ISSUES})}</p>
          )}
        </div>
        {isDocViewPdfAndXlsxReportEnabled && this.renderExportModal(reportFormats.XLSX)}
        {isDocViewPdfAndXlsxReportEnabled && this.renderExportModal(reportFormats.PDF)}
        {this.renderFooter()}
      </div>
    );
  }
}

IssuesPanel.displayName = 'IssuesPanel';

IssuesPanel.propTypes = {
  loading: PropTypes.bool.isRequired,
  error: PropTypes.bool,
  issues: issueWrapperArrayType.isRequired,
  fetchParams: issuesFetchParamsType.isRequired,
  fetchIssuesAction: PropTypes.func.isRequired,
  changeLocationAction: PropTypes.func.isRequired,
  pushpinWorkflowEnabled: PropTypes.bool.isRequired,
  document: documentType.isRequired,
  canCreateIssue: PropTypes.bool.isRequired,
  shouldShowProjectSetupInProgressPlaceholder: PropTypes.bool.isRequired,
  createReportAction: PropTypes.func.isRequired,
  trackEvent: PropTypes.func.isRequired,
  viewType: PropTypes.string.isRequired,
  isCreatingReport: PropTypes.bool.isRequired
};
